import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { MILESTONES } from "@/lib/content";

export default function Journey() {
  usePageMeta(
    "Our Journey — SKN Group of Companies | 1928 to Today",
    "The SKN Group timeline: from a 1928 Swadeshi-era venture near Howrah Bridge to a diversified conglomerate — SKNBM trademark 1942, carpets 1977, footwear 1989, Youvora 2022."
  );

  return (
    <div data-testid="journey-page">
      <section className="pt-40 md:pt-48 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">The Timeline</p>
            <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              Our Journey <span className="italic bronze">So Far</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              From a modest retail venture to a diversified business network, SKN
              Group's journey demonstrates nearly a century of entrepreneurial vision
              and strategic growth across multiple industries.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-testid="journey-timeline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          {/* Central line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-[#B8997A]/40" aria-hidden="true" />

          <div className="space-y-16 md:space-y-24">
            {MILESTONES.map((m, i) => (
              <div
                key={m.year}
                data-testid={`milestone-${m.year}`}
                className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-20 pl-14 md:pl-0 ${
                  i % 2 === 0 ? "" : ""
                }`}
              >
                {/* Node */}
                <span className="absolute left-6 md:left-1/2 top-3 -translate-x-1/2 w-3 h-3 bg-[#B8997A] rounded-full ring-4 ring-background" aria-hidden="true" />

                <div className={i % 2 === 0 ? "md:text-right md:pr-4" : "md:order-2 md:pl-4"}>
                  <Reveal y={20}>
                    <p className="font-display text-6xl md:text-8xl font-medium tracking-tight text-foreground/90">
                      {m.year}
                    </p>
                    <p className="mt-2 text-xs uppercase tracking-[0.3em] text-[#B8997A]">{m.title}</p>
                  </Reveal>
                </div>
                <div className={i % 2 === 0 ? "md:pl-4" : "md:order-1 md:text-right md:pr-4"}>
                  <Reveal y={20} delay={0.1}>
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-lg md:inline-block">
                      {m.text}
                    </p>
                  </Reveal>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ink-section py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl leading-[1.05]">
              The next chapter is <span className="italic bronze">being written</span>
            </h2>
            <Link
              to="/contact"
              data-testid="journey-contact-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#F7F5F0] text-[#0B0F14] px-8 py-4 text-sm font-medium hover:bg-[#B8997A] transition-colors duration-300"
            >
              Get in Touch <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
